import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { isAuthenticated, getAdminAuthHeaders } from '../utils/auth';

export type ColorMode = 'light' | 'dark';
export type DesignMode = 'classic' | 'modern';

interface ThemeContextValue {
  colorMode: ColorMode;
  dark: boolean;
  toggleColorMode: () => void;
  setColorMode: (mode: ColorMode) => void;

  /** The design mode currently applied (visitor override, or admin default). */
  designMode: DesignMode;
  /** Whether this visitor has overridden the site-wide default for themselves. */
  isDesignOverridden: boolean;
  /** Pass `null` to clear the override and go back to following the admin default. */
  setDesignOverride: (mode: DesignMode | null) => void;

  /** The site-wide default, set by an admin. */
  adminDefaultDesignMode: DesignMode;
  /** Admin-only: persist a new site-wide default design mode. */
  setAdminDefaultDesignMode: (mode: DesignMode) => Promise<boolean>;

  isAdmin: boolean;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const COLOR_MODE_KEY = 'colorMode';
const DESIGN_OVERRIDE_KEY = 'designModeOverride';

function readColorMode(): ColorMode {
  if (typeof window === 'undefined') return 'dark';
  const saved = localStorage.getItem(COLOR_MODE_KEY);
  return saved === 'light' || saved === 'dark' ? saved : 'dark';
}

function readDesignOverride(): DesignMode | null {
  if (typeof window === 'undefined') return null;
  const saved = localStorage.getItem(DESIGN_OVERRIDE_KEY);
  return saved === 'classic' || saved === 'modern' ? saved : null;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [colorMode, setColorModeState] = useState<ColorMode>(readColorMode);
  const [adminDefaultDesignMode, setAdminDefaultDesignModeState] = useState<DesignMode>('classic');
  const [designOverride, setDesignOverrideState] = useState<DesignMode | null>(readDesignOverride);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    setIsAdmin(isAuthenticated());
  }, []);

  // Apply color mode to <html> so every Tailwind `dark:` class works app-wide.
  useEffect(() => {
    document.documentElement.classList.toggle('dark', colorMode === 'dark');
    localStorage.setItem(COLOR_MODE_KEY, colorMode);
  }, [colorMode]);

  const designMode = designOverride ?? adminDefaultDesignMode;

  // Apply design mode to <html> so global "modern" CSS refinements can target it.
  useEffect(() => {
    document.documentElement.classList.toggle('design-modern', designMode === 'modern');
    document.documentElement.classList.toggle('design-classic', designMode === 'classic');
  }, [designMode]);

  // Fetch the admin-configured site-wide default once on load.
  useEffect(() => {
    let cancelled = false;
    fetch('/api/siteDesign')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled) return;
        if (data?.designMode === 'modern' || data?.designMode === 'classic') {
          setAdminDefaultDesignModeState(data.designMode);
        }
      })
      .catch(() => {
        // Network/dev-only environments without functions running: keep default.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleColorMode = useCallback(() => {
    setColorModeState((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  const setColorMode = useCallback((mode: ColorMode) => setColorModeState(mode), []);

  const setDesignOverride = useCallback((mode: DesignMode | null) => {
    setDesignOverrideState(mode);
    if (mode) {
      localStorage.setItem(DESIGN_OVERRIDE_KEY, mode);
    } else {
      localStorage.removeItem(DESIGN_OVERRIDE_KEY);
    }
  }, []);

  const setAdminDefaultDesignMode = useCallback(async (mode: DesignMode) => {
    try {
      const res = await fetch('/api/siteDesign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAdminAuthHeaders() },
        body: JSON.stringify({ designMode: mode }),
      });
      if (!res.ok) return false;
      setAdminDefaultDesignModeState(mode);
      return true;
    } catch {
      return false;
    }
  }, []);

  const value: ThemeContextValue = {
    colorMode,
    dark: colorMode === 'dark',
    toggleColorMode,
    setColorMode,
    designMode,
    isDesignOverridden: designOverride !== null,
    setDesignOverride,
    adminDefaultDesignMode,
    setAdminDefaultDesignMode,
    isAdmin,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
